// Renders an AI answer string with known location names wrapped in
// clickable links. Why this exists: AI replies mention countries ("Italy",
// "Portugal") and Italian comuni ("Trento", "Bolzano") all the time, but
// without a click target the user has to copy-paste them into the search
// box. Linkify removes that friction — click a country → land on the map
// for that country; click a comune → open its details panel.
//
// Scope (V0):
//   - Country names (en + ru) from /data/countries.json — works on every
//     surface.
//   - Italian comuni from the cached full index — only when an enrollment
//     hook says "we have the index loaded" (details / compare surfaces).
//
// Matching strategy:
//   - Tokens are scanned greedily; longer matches win first so
//     "Trento Alto Adige" beats "Trento".
//   - Case-insensitive but boundary-aware (must hit start/end of a word so
//     "Italy" doesn't match inside "Italyfication").
//   - Per-name limit: each unique name is linked at most twice per answer
//     to avoid noise; the rest stays as plain text.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, queries } from '@/data';
import { useDetailsStore } from '@/features/details/store';
import { emit } from '@/lib/events';

interface Props {
  text: string;
  /** Surface guides preferred target: 'details' opens panel for comuni,
   *  'quiz_result' / 'recommendations' navigate to country page. */
  surface?: 'quiz_result' | 'recommendations' | 'map' | 'details' | 'next_steps' | 'quiz';
}

interface Hit {
  start: number;
  end: number;
  kind: 'country' | 'comune';
  payload: string; // country code or istat
  label: string; // canonical name for analytics
}

const MAX_PER_NAME = 2;
// Words too generic to safely link (some country names overlap common
// English/Russian words). Filtered out at lookup-time, not at match-time
// — they still render, just without the link wrapper.
const STOPLIST = new Set<string>(['и', 'in', 'на', 'для', 'или', 'or']);

export function LinkedAnswerText({ text, surface }: Props) {
  const { data: countriesFile } = useQuery(countriesQuery());
  const { data: meta } = useQuery(queries.meta());
  // Comune linkify is opt-in: enable only on surfaces where the full index
  // is likely already cached. Cold-loading a 5 MB blob just to find a name
  // in a one-paragraph answer would tank perf. `details` is the only
  // surface where this is reliably hot today.
  const wantComuni = surface === 'details';
  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(meta?.version ?? ''),
    enabled: wantComuni && !!meta?.version,
  });

  const openComune = useDetailsStore((s) => s.open);
  const navigate = useNavigate();

  const hits = useMemo<Hit[]>(() => {
    if (!text) return [];
    const out: Hit[] = [];
    const countByName = new Map<string, number>();

    const tryPush = (start: number, end: number, kind: Hit['kind'], payload: string, label: string) => {
      const key = `${kind}:${label.toLowerCase()}`;
      const seen = countByName.get(key) ?? 0;
      if (seen >= MAX_PER_NAME) return;
      // Reject overlap with an already-recorded hit.
      if (out.some((h) => start < h.end && end > h.start)) return;
      out.push({ start, end, kind, payload, label });
      countByName.set(key, seen + 1);
    };

    const lowerText = text.toLowerCase();

    // Build a list of (canonical name, kind, payload) sorted longest-first
    // so "Russian Federation" gets matched before "Russia".
    const entries: Array<{ name: string; kind: Hit['kind']; payload: string }> = [];
    for (const c of countriesFile?.countries ?? []) {
      for (const variant of [c.name_en, c.name_ru]) {
        if (variant && !STOPLIST.has(variant.toLowerCase())) {
          entries.push({ name: variant, kind: 'country', payload: c.code });
        }
      }
    }
    if (wantComuni && fullIndex) {
      for (const c of fullIndex.byIstat.values()) {
        if (c.name && c.name.length >= 3 && !STOPLIST.has(c.name.toLowerCase())) {
          entries.push({ name: c.name, kind: 'comune', payload: c.istat });
        }
      }
    }
    entries.sort((a, b) => b.name.length - a.name.length);

    for (const e of entries) {
      const lname = e.name.toLowerCase();
      // Multiple occurrences allowed up to MAX_PER_NAME; cheap scan.
      let from = 0;
      while (true) {
        const idx = lowerText.indexOf(lname, from);
        if (idx < 0) break;
        const before = idx === 0 ? ' ' : lowerText[idx - 1];
        const after = idx + lname.length >= lowerText.length ? ' ' : lowerText[idx + lname.length];
        const isBoundary = isWordBoundary(before) && isWordBoundary(after);
        if (isBoundary) {
          tryPush(idx, idx + lname.length, e.kind, e.payload, e.name);
        }
        from = idx + lname.length;
      }
    }

    return out.sort((a, b) => a.start - b.start);
  }, [text, countriesFile, fullIndex, wantComuni]);

  if (hits.length === 0) {
    return (
      <p
        className="whitespace-pre-wrap leading-relaxed"
        data-testid="ai-linked-answer"
      >
        {text}
      </p>
    );
  }

  const segments: Array<{ kind: 'text' | 'link'; content: string; hit?: Hit }> = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start > cursor) {
      segments.push({ kind: 'text', content: text.slice(cursor, h.start) });
    }
    segments.push({ kind: 'link', content: text.slice(h.start, h.end), hit: h });
    cursor = h.end;
  }
  if (cursor < text.length) {
    segments.push({ kind: 'text', content: text.slice(cursor) });
  }

  const handleClick = (hit: Hit) => {
    emit('ai:location_clicked', {
      kind: hit.kind,
      payload: hit.payload,
      label: hit.label,
      surface: surface ?? 'unknown',
    });
    if (hit.kind === 'comune') {
      openComune(hit.payload);
      return;
    }
    // Countries: prefer landing on the country page for context; the user
    // can pick the map / quiz from there.
    navigate(`/countries#${hit.payload}`);
  };

  return (
    <p
      className="whitespace-pre-wrap leading-relaxed"
      data-testid="ai-linked-answer"
    >
      {segments.map((s, i) => {
        if (s.kind === 'text' || !s.hit) {
          return <span key={i}>{s.content}</span>;
        }
        return (
          <button
            key={i}
            type="button"
            onClick={() => handleClick(s.hit!)}
            className="rounded px-0.5 text-terra-700 underline decoration-terra-300 decoration-dotted underline-offset-2 hover:bg-terra-50 hover:decoration-terra-600"
            data-testid={`ai-link-${s.hit.kind}-${s.hit.payload}`}
            title={s.hit.kind === 'comune' ? 'Open comune details' : 'Open country page'}
          >
            {s.content}
          </button>
        );
      })}
    </p>
  );
}

function isWordBoundary(ch: string): boolean {
  if (!ch) return true;
  // letter / digit → not a boundary. We need to support Cyrillic letters too,
  // so the test goes beyond the ASCII `\w`.
  return !/[a-zA-Z0-9а-яёА-ЯЁ]/.test(ch);
}
